import {
  INewTumRegister,
  IParamsGetNewTumRegister,
} from 'app/api/newTumRegister/model'

import R from 'app/assets/R'
import { PermissionEnum } from 'app/common/enum'
import { BaseButton } from 'app/components/common/BaseButton'
import { BasePopconfirm } from 'app/components/common/BasePopconfirm'
import { BaseSpace } from 'app/components/common/BaseSpace'
import BaseText from 'app/components/common/BaseText'
import { TRADE_UNION_MEMBER_DETAILS_PATH } from 'app/components/router/route-path'
import { RenderValueTable } from 'app/components/tables/BaseTableManagement/hook'
import { convertColumnTable } from 'app/components/tables/BaseTableReport/hook'
import { useVerifyPermission } from 'app/helpers/permission.helper'
import { useDebounce, usePagination } from 'app/hook'
import {
  useApproveTum,
  useGetNewTumRegister,
} from 'app/react-query/hook/newTumRegister'
import { IResponseQueryList } from 'app/react-query/model/common'
import { Pagination, initialPagination } from 'parkway-web-common'
import { useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router'
import { ENewTumRegisterStatus, IDataTable } from './type'
import { ISort } from 'app/common/interface'
import { SorterResult } from 'antd/es/table/interface'

interface IProps {
  status: ENewTumRegisterStatus
}

export const useHook = ({ status }: IProps) => {
  const { t } = useTranslation()
  const navigate = useNavigate()

  const { user, verifyPermissionAndRedirect, verifyPermission } =
    useVerifyPermission()
  const [pagination, setPagination] = useState<Pagination>(initialPagination)
  const [filter, setFilter] = useState<IParamsGetNewTumRegister>({})
  const [sorts, setSort] = useState<ISort[]>([])

  const filterDebounce = useDebounce(filter, 500)

  const { flattenDataList } = usePagination()
  const { data: dataApi, isLoading } = useGetNewTumRegister({
    search: filterDebounce?.search,
    page: pagination?.current,
    limit: pagination?.pageSize,
    status: status,
    userId: user?.id,
    sorts,
  })

  const flattenData = useMemo(() => {
    const res: IResponseQueryList<INewTumRegister[]> = flattenDataList(dataApi)
    return res
  }, [dataApi])

  const data: IDataTable[] = useMemo(() => {
    return (
      flattenData?.data?.map((item, index) => ({
        ...item,
        key: index + 1,
      })) ?? []
    )
  }, [flattenData])

  const total = useMemo(() => {
    return flattenData?.total ?? data?.length
  }, [flattenData, data])

  const onChangeFilter = (newFilter: IParamsGetNewTumRegister) => {
    setFilter(newFilter)
    setPagination(prev => ({ ...prev, current: 1 }))
  }

  useEffect(() => {
    verifyPermissionAndRedirect(PermissionEnum.CreateNewTumRegister)
  }, [user])

  const isPermissionUpdate = useMemo(() => {
    return verifyPermission(PermissionEnum.UpdateNewTumRegister)
  }, [user])

  const {
    mutateAsync: mutateAsyncChangeStatus,
    isLoading: isLoadingChangeStatus,
  } = useApproveTum()

  const onPressChangeStatus = async (
    id?: number,
    statusOther?: ENewTumRegisterStatus,
  ) => {
    if (!id || !statusOther) return
    try {
      await mutateAsyncChangeStatus({
        id,
        status: statusOther,
      })
    } catch (error) {
      console.log({ error })
    }
  }

  const columns = useMemo(() => {
    return [
      convertColumnTable<INewTumRegister>({
        key: 'fullName',
        title: t(R.strings.full_name),
        fixed: 'left',
        render: (_, record) => {
          return (
            <RenderValueTable
              type="Link"
              value={record?.fullName}
              onClick={() =>
                navigate(TRADE_UNION_MEMBER_DETAILS_PATH, {
                  state: {
                    tum: record,
                  },
                })
              }
            />
          )
        },
      }),
      convertColumnTable<INewTumRegister>({
        key: 'mql',
        title: t(R.strings.id_management),
        fixed: 'left',
        render: (_, record) => {
          return (
            <BaseText fontWeight="semibold" children={record?.mql ?? '-'} />
          )
        },
      }),
      convertColumnTable<INewTumRegister>({
        key: 'email',
        title: t(R.strings.email),
        render: (_, record) => {
          return (
            <BaseText
              fontWeight="medium"
              opacity="0.7"
              children={record?.email ?? '-'}
            />
          )
        },
      }),
      convertColumnTable<INewTumRegister>({
        key: 'tenDonVi',
        title: 'Đơn vị UEH',
        render: text => {
          return <BaseText fontWeight="medium" opacity="0.7" children={text} />
        },
      }),

      convertColumnTable<INewTumRegister>({
        key: 'gioiTinh',
        title: t(R.strings.gender),
        render: (_, record) => {
          return (
            <BaseText
              fontWeight="medium"
              opacity="0.7"
              children={record?.gioiTinh ?? '-'}
            />
          )
        },
      }),

      convertColumnTable<INewTumRegister>({
        key: 'activityEngagement',
        title: t(R.strings.status_union_activities),
        render: (_, record) => {
          return (
            <BaseText
              fontWeight="medium"
              opacity="0.7"
              children={record?.activityEngagement?.name ?? '-'}
            />
          )
        },
      }),

      convertColumnTable<INewTumRegister>({
        key: 'school',
        title: 'Công đoàn bộ phận',
        render: (_, record) => {
          return (
            <BaseText
              fontWeight="medium"
              opacity="0.7"
              children={record?.school?.name ?? '-'}
            />
          )
        },
      }),

      convertColumnTable<INewTumRegister>({
        key: 'created_by',
        title: 'Người tạo',
        render: (_, record) => {
          return (
            <BaseSpace size={4}>
              <BaseText
                fontWeight="semibold"
                children={record?.created_by?.full_name ?? '-'}
              />
              <BaseText
                fontWeight="medium"
                opacity="0.7"
                children={record?.created_by?.email ?? '-'}
              />
            </BaseSpace>
          )
        },
      }),

      convertColumnTable<INewTumRegister>({
        key: 'dienThoaiDiDong',
        title: t(R.strings.phone),
        render: (_, record) => {
          console.log(isPermissionUpdate, status)

          return (
            <BaseText
              fontWeight="medium"
              opacity="0.7"
              children={record?.dienThoaiDiDong ?? '-'}
            />
          )
        },
      }),
      ...(status === ENewTumRegisterStatus.REJECTED ? [
        convertColumnTable<INewTumRegister>({
          key: 'reason',
          title: 'Lý do từ chối',
          render: (_, record) => {
            return (
              <BaseText
                fontWeight="medium"
                opacity="0.7"
                colorText='statesRedColor'
                children={record?.reason ?? '-'}
              />
            )
          },
        }),
      ] : []),

      ...(isPermissionUpdate && status
        ? [
            convertColumnTable<INewTumRegister>({
              key: 'id',
              width: '10%',
              fixed: 'right',
              render: (_, record) => {
                switch (status) {
                  case ENewTumRegisterStatus.WAITING:
                    return (
                      <BasePopconfirm
                        onConfirm={() =>
                          onPressChangeStatus(
                            +(record?.id ?? 0),
                            ENewTumRegisterStatus.CANCELED,
                          )
                        }
                        title={'Bạn có chắc chắn muốn huỷ khai báo?'}
                      >
                        <BaseButton children={'Huỷ khai báo'} />
                      </BasePopconfirm>
                    )
                  case ENewTumRegisterStatus.CANCELED:
                    return (
                      <BasePopconfirm
                        onConfirm={() =>
                          onPressChangeStatus(
                            +(record?.id ?? 0),
                            ENewTumRegisterStatus.WAITING,
                          )
                        }
                        title={'Bạn có chắc chắn muốn khôi phục khai báo?'}
                      >
                        <BaseButton children={'Khôi phục'} />
                      </BasePopconfirm>
                    )
                }
              },
            }),
          ]
        : []),
    ]
  }, [t, isPermissionUpdate, status])

  const handleTableChange = (
    pagination: Pagination,
    _,
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    sorter: SorterResult<any> | SorterResult<any>[],
  ) => {
    setPagination(pagination)

    let sortParams: { field: string; order: 'ASC' | 'DESC' }[] = []

    if (Array.isArray(sorter)) {
      // Nếu sorter là mảng (multi-sort)
      sortParams = sorter
        .filter(s => s.order) // Lọc ra những cột có thứ tự sắp xếp
        .map(s => ({
          field: s.field as string,
          order: s.order === 'ascend' ? 'ASC' : 'DESC',
        }))
    } else if (sorter.order) {
      // Nếu chỉ có một cột được sort
      sortParams = [
        {
          field: sorter.field as string,
          order: sorter.order === 'ascend' ? 'ASC' : 'DESC',
        },
      ]
    }

    setSort(sortParams)
  }

  return {
    columns,
    pagination: {
      ...pagination,
      total,
    },
    onChangeFilter,
    filter,
    isLoading: isLoading || isLoadingChangeStatus,
    total,
    handleChangePagination: handleTableChange,
    data,
    isPermissionUpdate,
  }
}
